import Phaser from 'phaser';
import { ethers } from 'ethers';
import { SCENES } from '../types/game';
import type { LeaderboardEntry } from '../types/game';
import { AudioManager } from '../utils/AudioManager';
import { GAME_CONFIG } from '../lib/game-config';
import { KINGSOMNI_LEADERBOARD_ABI } from '../lib/contract-abi';

const SOMNIA_RPC_URL = import.meta.env.VITE_RPC_URL || 'https://dream-rpc.somnia.network/';
const PLAYERS_PAGE_SIZE = 200;
const LEADERBOARD_BATCH_SIZE = 20;

interface OnchainLeaderboardRecord {
  address: string;
  totalScore: number;
  totalKills: number;
  gamesPlayed: number;
}

export class LeaderboardScene extends Phaser.Scene {
  private userData: any = null;
  private leaderboardData: LeaderboardEntry[] = [];
  private loading = false;

  constructor() {
    super({ key: SCENES.LEADERBOARD });
  }

  init(data?: any) {
    if (data && data.user) {
      this.userData = data.user;
    }
  }

  preload() {
    AudioManager.getInstance().preloadAudio(this);
  }

  create() {
    const { width, height } = this.cameras.main;

    const currentMusic = AudioManager.getInstance().getCurrentMusicKey();
    if (currentMusic !== 'main-menu-music') {
      AudioManager.getInstance().playMainMenuMusic(this);
    }

    this.add.image(width / 2, height / 2, 'leaderboard_background');

    const title = this.add.text(width / 2, 50, 'KINGSOMNI LEADERBOARD', {
      fontFamily: 'Minecraft',
      fontSize: '36px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);

    this.createBackButton();
    void this.loadLeaderboard();
  }

  private createBackButton() {
    const { width, height } = this.cameras.main;
    const backButton = this.add.image(width / 2, height - 50, 'exit_btn');
    backButton.setScale(0.8).setInteractive({ cursor: 'pointer' });

    backButton.on('pointerdown', () => this.scene.start(SCENES.HOME, { user: this.userData }));
    backButton.on('pointerover', () => backButton.setTint(0xcccccc));
    backButton.on('pointerout', () => backButton.clearTint());
  }

  private async loadLeaderboard() {
    if (this.loading) return;
    this.loading = true;

    const { width } = this.cameras.main;
    const loadingText = this.add.text(width / 2, 200, 'Loading on-chain leaderboard...', {
      fontFamily: 'Minecraft',
      fontSize: '18px',
      color: '#a3a3a3',
    });
    loadingText.setOrigin(0.5);

    try {
      const leaderboardAddress = GAME_CONFIG.CONTRACTS.LEADERBOARD;
      if (!leaderboardAddress || leaderboardAddress === ethers.ZeroAddress) {
        throw new Error('Leaderboard contract address is not configured');
      }

      const provider = new ethers.JsonRpcProvider(SOMNIA_RPC_URL);
      const contract = new ethers.Contract(
        leaderboardAddress,
        KINGSOMNI_LEADERBOARD_ABI,
        provider,
      );

      const players = await this.fetchPlayers(contract);
      const records = await this.fetchLeaderboardRecords(contract, players);
      const rankedRecords = records
        .filter((record) => record.totalScore > 0 || record.gamesPlayed > 0)
        .sort((a, b) => {
          if (b.totalScore !== a.totalScore) return b.totalScore - a.totalScore;
          if (b.totalKills !== a.totalKills) return b.totalKills - a.totalKills;
          return b.gamesPlayed - a.gamesPlayed;
        });

      this.leaderboardData = rankedRecords.map((record, index) => ({
        rank: index + 1,
        username: this.formatAddressLabel(record.address),
        score: record.totalScore,
        address: record.address,
        timestamp: Date.now(),
      }));

      loadingText.destroy();
      this.displayLeaderboard();
    } catch (error) {
      console.error('Error loading leaderboard:', error);
      loadingText.setText('Failed to load leaderboard');
    } finally {
      this.loading = false;
    }
  }

  private async fetchPlayers(contract: ethers.Contract): Promise<string[]> {
    const totalPlayers = Number(await contract.getPlayersCount());
    if (!Number.isFinite(totalPlayers) || totalPlayers <= 0) {
      return [];
    }

    const players: string[] = [];
    for (let offset = 0; offset < totalPlayers; offset += PLAYERS_PAGE_SIZE) {
      const pageSize = Math.min(PLAYERS_PAGE_SIZE, totalPlayers - offset);
      const page: string[] = await contract.getPlayers(offset, pageSize);
      for (const player of page) {
        players.push(ethers.getAddress(player));
      }
    }

    return players;
  }

  private async fetchLeaderboardRecords(
    contract: ethers.Contract,
    addresses: string[],
  ): Promise<OnchainLeaderboardRecord[]> {
    const records: OnchainLeaderboardRecord[] = [];

    for (let index = 0; index < addresses.length; index += LEADERBOARD_BATCH_SIZE) {
      const batch = addresses.slice(index, index + LEADERBOARD_BATCH_SIZE);
      const batchRecords = await Promise.all(
        batch.map(async (address) => {
          const data = await contract.leaderboards(address);
          return {
            address,
            totalScore: Number(data.totalScore),
            totalKills: Number(data.totalKills),
            gamesPlayed: Number(data.gamesPlayed),
          };
        }),
      );

      records.push(...batchRecords);
    }

    return records;
  }

  private formatAddressLabel(address: string): string {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }

  private displayLeaderboard() {
    const { width } = this.cameras.main;
    const startY = 140;
    const entryHeight = 40;
    const maxEntries = 10;

    if (this.leaderboardData.length === 0) {
      this.add
        .text(width / 2, 200, 'No scores available yet', {
          fontSize: '18px',
          color: '#a3a3a3',
        })
        .setOrigin(0.5);
      return;
    }

    this.add.graphics().fillStyle(0x1a1a2e, 0.9).fillRoundedRect(width / 2 - 350, startY - 40, 700, 35, 5);
    this.add.text(width / 2 - 300, startY - 22, 'RANK', { fontSize: '14px', color: '#ffffff', fontStyle: 'bold' }).setOrigin(0, 0.5);
    this.add.text(width / 2 - 100, startY - 22, 'PLAYER', { fontSize: '14px', color: '#ffffff', fontStyle: 'bold' }).setOrigin(0, 0.5);
    this.add.text(width / 2 + 250, startY - 22, 'SCORE', { fontSize: '14px', color: '#ffffff', fontStyle: 'bold' }).setOrigin(1, 0.5);

    this.leaderboardData.slice(0, maxEntries).forEach((entry, index) => {
      const y = startY + index * entryHeight;
      const isCurrentUser =
        typeof this.userData?.address === 'string' &&
        this.userData.address.toLowerCase() === entry.address.toLowerCase();

      this.add
        .graphics()
        .fillStyle(isCurrentUser ? 0x22c55e : index % 2 === 0 ? 0x1a1a2e : 0x0f0f23, 0.7)
        .fillRoundedRect(width / 2 - 350, y - 15, 700, 30, 5);

      const rankText = entry.rank === 1 ? '1st' : entry.rank === 2 ? '2nd' : entry.rank === 3 ? '3rd' : `#${entry.rank}`;
      this.add.text(width / 2 - 300, y, rankText, { fontSize: '16px', color: '#ffffff' }).setOrigin(0, 0.5);
      this.add
        .text(width / 2 - 100, y, isCurrentUser ? `You (${entry.username})` : entry.username || 'Unknown', {
          fontSize: '16px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5);
      this.add
        .text(width / 2 + 250, y, entry.score.toLocaleString(), {
          fontSize: '16px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(1, 0.5);
    });
  }
}
